import "./dashboard.scss";

import { DashboardPatientDto } from "@api/api";
import UpcomingSessionWidget from "@domain/upcoming-session/upcoming-session-widget/upcoming-session-widget";
import { useFilterUtilServiceContext } from "@domain/util-service/filter-util-service/filter-util-service.context";
import {
  useQyGetDashboard,
  useQyGetPatientSummary,
  useQyGetTotalExpectedBookingRevenuePatients,
  useQyGetTotalPaidPatients,
  useQyGetTotalPendingPatients,
} from "@factory/query/dashboard.query";
import { useQyGetUtilCategory } from "@factory/query/util-category.query";
import { DashboardModel } from "@shared/models/dashboard-model";
import { LabelValue } from "@shared/models/label-value.interface";
import DashboardCard from "@shared/ui/dashboard-card/dashboard-card";
import DashboardCardSkeleton from "@shared/ui/dashboard-card/dashboard-card-skeleton";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { numberFormat } from "@shared/utility/formats/number-format";
import { isTextAboutMoney } from "@shared/utility/is-text-about-money";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Sidebar } from "primereact/sidebar";
import { useCallback, useMemo, useState } from "react";
import { FaChartPie, FaUserMd } from "react-icons/fa";

import { appConfig } from "../../app.config";

export interface DashboardProps {}

interface PatientInfo {
  patient: string;
  name: string;
  count?: number;
}

type SummaryCardType =
  "expected-booking-revenue" | "total-paid" | "total-pending" | null;

// Placeholder card counts while the dashboard query loads (grid is 4 per row)
const SUMMARY_SKELETON_COUNT = 4;
const SERVICE_SKELETON_COUNT = 8;

// Summary cards in display order, by API label. Detail cards are hidden until
// the user opts in, and come after the main row so its layout never shifts.
const SUMMARY_MAIN_LABELS = [
  "Total Expected Booking Revenue",
  "Total Pending or Collectible",
  "Total Paid",
  "Total Patient",
];
const SUMMARY_DETAIL_LABELS = ["Total Discount", "Net Revenue"];

// Display names for summary cards whose API label differs from what the UI shows
const SUMMARY_LABEL_DISPLAY: Record<string, string> = {
  "Total Paid": "Net Revenue",
  "Total Pending or Collectible": "Total Collectible / Pending Payment",
  "Net Revenue": "Expected Net Revenue",
};
const getSummaryDisplayLabel = (label: string) =>
  SUMMARY_LABEL_DISPLAY[label] ?? label;

// Per-browser preference for the discount detail cards
const SHOW_DISCOUNT_DETAILS_KEY = "dashboard.showDiscountDetails";
const readShowDiscountDetails = () => {
  try {
    return localStorage.getItem(SHOW_DISCOUNT_DETAILS_KEY) === "true";
  } catch {
    return false;
  }
};
const writeShowDiscountDetails = (value: boolean) => {
  try {
    localStorage.setItem(SHOW_DISCOUNT_DETAILS_KEY, String(value));
  } catch {
    // Storage unavailable; the toggle still works for this session
  }
};

// Only this client defaults the dashboard to today's figures
const DEFAULT_DAY_FILTER = appConfig.client === "veaesthetics" ? "TODAY" : "";

const renderCardSkeletons = (count: number, keyPrefix: string) =>
  [...Array(count)].map((_, index) => (
    <DashboardCardSkeleton key={`${keyPrefix}-skeleton-${index}`} />
  ));

export function Dashboard() {
  const { formatDateLocal } = useDateTimeContext();
  const [selectedDayFilter, setSelectedDayFilter] =
    useState(DEFAULT_DAY_FILTER);
  const [dateFilter, setDateFilter] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [patientListVisible, setPatientListVisible] = useState(false);
  const [selectedServicePatients, setSelectedServicePatients] = useState<
    PatientInfo[]
  >([]);
  const [selectedServiceName, setSelectedServiceName] = useState("");
  const [showDiscountDetails, setShowDiscountDetails] = useState(
    readShowDiscountDetails,
  );

  const toggleDiscountDetails = () => {
    setShowDiscountDetails((current) => {
      writeShowDiscountDetails(!current);
      return !current;
    });
  };

  // Sidebar state for summary cards
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [sidebarTitle, setSidebarTitle] = useState("");
  const [selectedSummaryType, setSelectedSummaryType] =
    useState<SummaryCardType>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    null,
  );

  // Pagination state for sidebar
  const [first, setFirst] = useState(0);
  const [rowLimit, setRowLimit] = useState(10);

  const { getFilterEntityValues } = useFilterUtilServiceContext();
  const { category: contextCategory } = getFilterEntityValues();

  const { data: categoryData } = useQyGetUtilCategory("", 99999);
  const categoryOptions = useMemo(() => {
    if (!categoryData?.data) return [];
    return LabelValue.MapArray(
      categoryData.data,
      (label) => label.name || "-",
      (value) => value.code,
    );
  }, [categoryData]);

  const dayFilterOption = [
    { label: "Today", value: "TODAY" },
    { label: "Yesterday", value: "YESTERDAY" },
    { label: "Week", value: "WEEK" },
    { label: "Month", value: "MONTH" },
  ] as LabelValue[];
  const getDateRange = useCallback(() => {
    if (dateFilter && !!dateFilter?.[0] && !!dateFilter?.[1]) {
      const start = formatDateLocal(
        dateFilter?.[0] ?? "",
        undefined,
        "yyyy-MM-dd",
      );
      const end = formatDateLocal(
        dateFilter?.[1] ?? "",
        undefined,
        "yyyy-MM-dd",
      );
      return {
        start,
        end,
      };
    }

    return null;
  }, [dateFilter]);

  const dateRange = getDateRange();
  const startDate = dateRange?.start;
  const endDate = dateRange?.end;
  // "Clear" sets "" while the dropdown's clear icon sets undefined; both mean
  // "no preset" to the API, so share one cache entry instead of two requests.
  const dayFilter = selectedDayFilter || undefined;

  const effectiveCategory = contextCategory || selectedCategory;

  const filterParams = useMemo(() => {
    if (effectiveCategory) {
      return { filter: JSON.stringify({ category: effectiveCategory }) };
    }
    return undefined;
  }, [effectiveCategory]);

  const colorArray = [
    "bg-green-200",
    "bg-blue-200",
    "bg-teal-200",
    "bg-amber-200",
    "bg-lime-200",
    "bg-cyan-200",
    "bg-rose-200",
    "bg-fuchsia-200",
    "bg-purple-200",
  ];
  const {
    data,
    isLoading: isLoadingDashboard,
    isError: isDashboardError,
    refetch: refetchDashboard,
  } = useQyGetDashboard("", filterParams, dayFilter, startDate, endDate);
  const dashboardData = data || [];
  // Failed with nothing cached to show: render an error with a retry action
  // instead of silently empty sections.
  const showDashboardError = isDashboardError && !data;

  // Query hooks for summary card patients
  const { data: expectedRevenuePatients, isLoading: isLoadingExpectedRevenue } =
    useQyGetTotalExpectedBookingRevenuePatients(
      dayFilter,
      startDate,
      endDate,
      selectedSummaryType === "expected-booking-revenue" && sidebarVisible,
    );

  const { data: paidPatients, isLoading: isLoadingPaid } =
    useQyGetTotalPaidPatients(
      dayFilter,
      startDate,
      endDate,
      selectedSummaryType === "total-paid" && sidebarVisible,
    );

  const { data: pendingPatients, isLoading: isLoadingPending } =
    useQyGetTotalPendingPatients(
      dayFilter,
      startDate,
      endDate,
      selectedSummaryType === "total-pending" && sidebarVisible,
    );

  const { data: patientSummary, isLoading: isLoadingPatientSummary } =
    useQyGetPatientSummary(
      selectedPatientId || "",
      dayFilter,
      startDate,
      endDate,
      !!selectedPatientId && sidebarVisible,
    );

  // Get the current patient list based on selected type
  const currentPatientList = useMemo(() => {
    switch (selectedSummaryType) {
      case "expected-booking-revenue":
        return expectedRevenuePatients || [];
      case "total-paid":
        return paidPatients || [];
      case "total-pending":
        return pendingPatients || [];
      default:
        return [];
    }
  }, [
    selectedSummaryType,
    expectedRevenuePatients,
    paidPatients,
    pendingPatients,
  ]);

  const isLoadingPatientList =
    isLoadingExpectedRevenue || isLoadingPaid || isLoadingPending;

  const { summaryCards, serviceCards } = useMemo(() => {
    const pickByLabels = (labels: string[]) =>
      labels.flatMap((label) => {
        const match = dashboardData.find((item) => item.label?.includes(label));
        return match ? [match] : [];
      });

    const main = pickByLabels(SUMMARY_MAIN_LABELS);
    const details = pickByLabels(SUMMARY_DETAIL_LABELS);
    const summary = showDiscountDetails ? [...main, ...details] : main;

    // Detail cards stay out of services even while hidden
    const services = dashboardData.filter(
      (item) => !main.includes(item) && !details.includes(item),
    );
    const sortedServices = services.sort(
      (a, b) => (b.value || 0) - (a.value || 0),
    );
    return { summaryCards: summary, serviceCards: sortedServices };
  }, [dashboardData, showDiscountDetails]);

  const handleServiceCardClick = useCallback((item: any) => {
    if (!item.patient_list) return;

    try {
      const patientListStr = item.patient_list;
      const patients: PatientInfo[] = [];

      const patientParts = patientListStr.split("},{");

      patientParts.forEach((part: string, index: number) => {
        let jsonStr = part;

        if (index === 0 && patientParts.length > 1) {
          jsonStr = part + "}";
        } else if (
          index === patientParts.length - 1 &&
          patientParts.length > 1
        ) {
          jsonStr = "{" + part;
        } else if (index > 0 && index < patientParts.length - 1) {
          jsonStr = "{" + part + "}";
        }

        try {
          const patient = JSON.parse(jsonStr);
          patients.push(patient);
        } catch (e) {
          console.warn("Failed to parse patient data:", jsonStr);
        }
      });

      setSelectedServicePatients(patients);
      setSelectedServiceName(item.label);
      setPatientListVisible(true);
    } catch (error) {
      console.error("Error parsing patient list:", error);
    }
  }, []);

  // Handle summary card click to open sidebar
  const handleSummaryCardClick = useCallback((label: string) => {
    let summaryType: SummaryCardType = null;

    if (label.includes("Total Expected Booking Revenue")) {
      summaryType = "expected-booking-revenue";
    } else if (label.includes("Total Paid")) {
      summaryType = "total-paid";
    } else if (label.includes("Total Pending or Collectible")) {
      summaryType = "total-pending";
    }

    if (summaryType) {
      setSelectedSummaryType(summaryType);
      setSidebarTitle(getSummaryDisplayLabel(label));
      setSelectedPatientId(null);
      setFirst(0);
      setSidebarVisible(true);
    }
  }, []);

  // Handle patient selection in sidebar
  const handlePatientSelect = useCallback((patient: DashboardPatientDto) => {
    setSelectedPatientId(patient.patient);
  }, []);

  // Handle back to patient list
  const handleBackToList = useCallback(() => {
    setSelectedPatientId(null);
  }, []);

  // Handle sidebar close
  const handleSidebarClose = useCallback(() => {
    setSidebarVisible(false);
    setSelectedSummaryType(null);
    setSelectedPatientId(null);
    setFirst(0);
  }, []);

  // Handle pagination change
  const onPageChange = useCallback((event: PaginatorPageChangeEvent) => {
    setFirst(event.first);
    setRowLimit(event.rows);
  }, []);

  const summaryDashboardArray = useMemo(() => {
    return summaryCards.map((item, index) => {
      const itemValue = isTextAboutMoney(item.label ?? "")
        ? currencyFormat(item.value ?? 0)
        : numberFormat(item.value || 0);

      return new DashboardModel(
        item.label ? getSummaryDisplayLabel(item.label) : "-",
        itemValue,
        <FaChartPie />,
        undefined,
        undefined,
        colorArray[index],
      );
    });
  }, [summaryCards]);

  const serviceDashboardArray = useMemo(() => {
    return serviceCards.map((item, index) => {
      const itemValue = numberFormat(item.value || 0);

      return new DashboardModel(
        item.label || "-",
        itemValue,
        <FaUserMd />,
        undefined,
        undefined,
        colorArray[(index + 4) % colorArray.length],
      );
    });
  }, [serviceCards]);

  const handleCardClick = useCallback(
    (event: React.MouseEvent) => {
      const cardElement = event.currentTarget as HTMLElement;
      const serviceIndex = cardElement.getAttribute("data-service-index");

      if (serviceIndex !== null) {
        const item = serviceCards[parseInt(serviceIndex)];
        if (item?.patient_list) {
          handleServiceCardClick(item);
        }
      }
    },
    [serviceCards, handleServiceCardClick],
  );

  // Handle clear filters
  const handleClear = () => {
    setSelectedDayFilter("");
    setDateFilter([]);
    setSelectedCategory("");
  };

  const handleDayFilterChange = (value: string) => {
    setSelectedDayFilter(value);
    if (value) {
      setDateFilter([]);
    }
  };

  const handleDateRangeChange = (value: any) => {
    setDateFilter(value);
    if (value && value[0]) {
      setSelectedDayFilter("");
    }
  };

  const dayFilterElement = (
    <Dropdown
      value={selectedDayFilter}
      onChange={(e) => setSelectedDayFilter(e.value)}
      options={dayFilterOption}
      placeholder="Select a day filter"
      className="w-full"
      showClear
      disabled={!!dateFilter?.[0]}
    />
  );
  const dateRangeElement = (
    <Calendar
      value={dateFilter}
      onChange={(e) => setDateFilter(e.value as any)}
      selectionMode="range"
      readOnlyInput
      hideOnRangeSelection
      className="w-full"
      disabled={!!selectedDayFilter}
    />
  );

  const categoryDropdownElement = (
    <Dropdown
      value={selectedCategory}
      onChange={(e) => setSelectedCategory(e.value)}
      options={categoryOptions}
      placeholder="Select category"
      className="w-full"
      showClear
      disabled={!!contextCategory}
    />
  );

  return (
    <div id="Dashboard" className="dashboard p-7">
      <h1 className="mb-4">Dashboard</h1>
      <section>
        <h4 className="text-gray-500">Filters</h4>
        <section className="flex justify-between gap-4 mb-6">
          {dayFilterElement}
          {dateRangeElement}
          {categoryDropdownElement}
          <Button
            className="block w-28"
            severity="secondary"
            outlined
            label="Clear"
            onClick={handleClear}
          />
        </section>
      </section>

      {showDashboardError && (
        <section className="flex items-center justify-between gap-4 border rounded-lg p-4 mb-6">
          <span className="text-gray-600">Unable to load dashboard data.</span>
          <Button
            label="Retry"
            icon="pi pi-refresh"
            severity="secondary"
            outlined
            onClick={() => refetchDashboard()}
          />
        </section>
      )}

      {!effectiveCategory && (
        <section className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-gray-700">Financial Summary</h4>
            <Button
              label={
                showDiscountDetails
                  ? "Hide discount details"
                  : "Show discount details"
              }
              icon={showDiscountDetails ? "pi pi-eye-slash" : "pi pi-eye"}
              size="small"
              text
              onClick={toggleDiscountDetails}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {isLoadingDashboard
              ? renderCardSkeletons(SUMMARY_SKELETON_COUNT, "summary")
              : summaryDashboardArray.map((item, id) => {
                  // Match on the API label; the card title is the display name
                  const apiLabel = summaryCards[id]?.label ?? "";
                  const isClickable =
                    apiLabel.includes("Total Expected Booking Revenue") ||
                    apiLabel.includes("Total Paid") ||
                    apiLabel.includes("Total Pending or Collectible");
                  return (
                    <div
                      key={`summary-${id}`}
                      onClick={
                        isClickable
                          ? () => handleSummaryCardClick(apiLabel)
                          : undefined
                      }
                      className={isClickable ? "cursor-pointer" : ""}
                    >
                      <DashboardCard model={item} />
                    </div>
                  );
                })}
          </div>
        </section>
      )}

      <UpcomingSessionWidget
        dateFilter={dayFilter}
        startDate={startDate}
        endDate={endDate}
        category={effectiveCategory || undefined}
      />

      <section>
        <h4 className="text-gray-700 mb-4">Top Services</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {isLoadingDashboard
            ? renderCardSkeletons(SERVICE_SKELETON_COUNT, "service")
            : serviceDashboardArray.map((item, id) => (
                <div
                  key={`service-${id}`}
                  data-service-index={id}
                  onClick={
                    serviceCards[id]?.patient_list ? handleCardClick : undefined
                  }
                  className={
                    serviceCards[id]?.patient_list ? "cursor-pointer" : ""
                  }
                >
                  <DashboardCard model={item} />
                </div>
              ))}
        </div>
      </section>

      <Dialog
        header={`Patients for ${selectedServiceName}`}
        visible={patientListVisible}
        style={{ width: "50vw", minWidth: "400px" }}
        onHide={() => setPatientListVisible(false)}
        modal
        draggable={false}
      >
        <div className="patient-list">
          {selectedServicePatients.length > 0 ? (
            <ul className="space-y-2">
              {selectedServicePatients.map((patient, index) => (
                <li key={index} className="p-3 border rounded-lg bg-gray-50">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">{patient.name}</span>
                    {patient.count !== undefined && (
                      <span className="text-sm text-gray-600 bg-gray-200 px-2 py-1 rounded">
                        {patient.count}x
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">No patients found for this service.</p>
          )}
        </div>
      </Dialog>

      {/* Sidebar for Summary Card Patients */}
      <Sidebar
        visible={sidebarVisible}
        onHide={handleSidebarClose}
        position="right"
        style={{ width: "500px" }}
        header={
          <div className="flex items-center gap-2">
            {selectedPatientId && (
              <Button
                icon="pi pi-arrow-left"
                text
                onClick={handleBackToList}
                className="p-0"
              />
            )}
            <span>{selectedPatientId ? "Patient Summary" : sidebarTitle}</span>
          </div>
        }
      >
        {!selectedPatientId ? (
          // Patient List View
          <div className="patient-list-sidebar">
            {isLoadingPatientList ? (
              <SkeletonList count={5} />
            ) : currentPatientList.length > 0 ? (
              <>
                <h5 className="mb-2">
                  Total: <b>{currentPatientList.length}</b>
                </h5>
                <DataTable
                  value={currentPatientList.slice(first, first + rowLimit)}
                  selectionMode="single"
                  onSelectionChange={(e) => handlePatientSelect(e.value)}
                >
                  {/* <Column field="patient" header="Patient Code" /> */}
                  <Column field="name" header="Name" />
                </DataTable>
                <Paginator
                  first={first}
                  rows={rowLimit}
                  totalRecords={currentPatientList.length}
                  rowsPerPageOptions={[10, 20, 30]}
                  onPageChange={onPageChange}
                />
              </>
            ) : (
              <p className="text-gray-500">No patients found.</p>
            )}
          </div>
        ) : (
          // Patient Summary View
          <div className="patient-summary">
            {isLoadingPatientSummary ? (
              <SkeletonList count={3} />
            ) : patientSummary ? (
              <div className="flex flex-col gap-4">
                <Card className="shadow-sm">
                  <div className="text-center">
                    <h3 className="text-xl font-semibold mb-1">
                      {patientSummary.name}
                    </h3>
                    {/* <p className="text-gray-500">{patientSummary.patient}</p> */}
                  </div>
                </Card>

                <Card className="shadow-sm bg-green-50">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">
                      Total Expected Booking
                    </span>
                    <span className="text-xl font-bold text-green-700">
                      {currencyFormat(patientSummary.total_expected_booking)}
                    </span>
                  </div>
                </Card>

                <Card className="shadow-sm bg-amber-50">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">
                      Total Collectible / Pending Payment
                    </span>
                    <span className="text-xl font-bold text-amber-700">
                      {currencyFormat(patientSummary.total_pending)}
                    </span>
                  </div>
                </Card>

                <Card className="shadow-sm bg-blue-50">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Net Revenue</span>
                    <span className="text-xl font-bold text-blue-700">
                      {currencyFormat(patientSummary.total_paid)}
                    </span>
                  </div>
                </Card>

                {showDiscountDetails && (
                  <>
                    <Card className="shadow-sm bg-orange-50">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Total Discount</span>
                        <span className="text-xl font-bold text-orange-700">
                          {currencyFormat(patientSummary.total_discount ?? 0)}
                        </span>
                      </div>
                    </Card>

                    <Card className="shadow-sm bg-teal-50">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">
                          Expected Net Revenue
                        </span>
                        <span className="text-xl font-bold text-teal-700">
                          {currencyFormat(
                            patientSummary.total_net_revenue ?? 0,
                          )}
                        </span>
                      </div>
                    </Card>
                  </>
                )}
              </div>
            ) : (
              <p className="text-gray-500">Patient summary not found.</p>
            )}
          </div>
        )}
      </Sidebar>
    </div>
  );
}
